import { ErrorMessageSection } from "./shared";

export function GuideContent() {
  return (
    <>
      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          What Exactly Is a Sugargoo Spreadsheet — And Why Should You Care?
        </h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          If you have spent any time browsing Sugargoo, you know the platform is massive. Thousands of products spread across countless categories, sellers, and price ranges make it both exciting and overwhelming. A <strong>Sugargoo Spreadsheet</strong> is the solution that experienced shoppers have been using for years — and now you can too.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          At its core, a Sugargoo Spreadsheet is a curated, organized collection of product links and shopping data formatted in a spreadsheet. Think of it as a highly efficient product catalog that someone has already put together for you. Instead of manually searching through Sugargoo for hours, you open a spreadsheet and instantly see the best finds — complete with prices, size information, direct links, and community ratings.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
          In 2026, Sugargoo Spreadsheets have evolved beyond simple product lists. The best spreadsheets now include shipping cost calculators, coupon code trackers, warehouse storage tips, and even haul planning templates — making them an indispensable tool for anyone serious about Sugargoo shopping.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          How a Sugargoo Spreadsheet Transforms Your Shopping Experience
        </h2>
        <div className="bg-background-100 rounded-xl p-5 mb-4 border border-background-200/60">
          <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-3">Shopping Without a Spreadsheet</h3>
          <p className="text-sm text-foreground-600 leading-relaxed">
            You open Sugargoo, type keywords into the search bar, and scroll through pages of results. You open 15 tabs. Half are not quite right. Two hours later, you have a cart with five items and a vague feeling you missed better deals somewhere.
          </p>
        </div>
        <div className="bg-primary-50 rounded-xl p-5 mb-6 border border-primary-200">
          <h3 className="font-heading text-lg font-semibold text-primary-800 mb-3">Shopping With a Sugargoo Spreadsheet</h3>
          <p className="text-sm text-primary-700 leading-relaxed">
            You open your Sugargoo Spreadsheet, filter by category — say, Hoodies. Instantly, you see 40 curated listings with prices, size charts, and quality notes. You scan through in minutes, click direct links, and you are done. Total time: 20 minutes. Total confidence: high.
          </p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          Key Categories in Our Sugargoo Spreadsheet
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {[
            { category: "Shoes", desc: "Latest Nike, Adidas sneaker releases to classic casual footwear with detailed sizing guides and authenticity notes from verified Sugargoo sellers." },
            { category: "Hoodies & Sweaters", desc: "Cozy essentials — graphic hoodies, premium cotton crewnecks, and designer-inspired sweaters from high-rated Sugargoo stores with community feedback." },
            { category: "T-Shirts", desc: "Minimalist basics to bold graphic tees organized by style, brand, and price tier for effortless browsing through curated Sugargoo product pages." },
            { category: "Jackets & Outerwear", desc: "Bombers, denim jackets, puffer coats and technical outerwear — each with temperature ratings, material notes, and direct Sugargoo purchase links." },
            { category: "Pants & Shorts", desc: "Jeans, cargo pants, joggers and tailored trousers with precise measurements and fit recommendations from experienced Sugargoo spreadsheet contributors." },
            { category: "Accessories & Bags", desc: "Watches, sunglasses, jewelry, backpacks, crossbody bags — complete with direct Sugargoo links and community-vetted quality assessments." },
          ].map((item) => (
            <div key={item.category} className="p-4 rounded-xl bg-background-100 border border-background-200/60">
              <h4 className="text-sm font-semibold text-foreground-800 mb-2">{item.category}</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          6 Pro Strategies for Sugargoo Spreadsheet Users
        </h2>
        <div className="space-y-5">
          {[
            { num: "01", title: "Start with a Clear Shopping Goal", desc: "Define what you are looking for before opening your spreadsheet — seasonal wardrobe, specific sneakers, or a large haul. A clear goal helps you focus on relevant categories and avoid impulse buys that inflate shipping costs without adding real value." },
            { num: "02", title: "Use Filters Aggressively", desc: "Filter by category, price range, size availability, and seller rating first — then browse. This habit alone saves 30+ minutes per session and prevents decision fatigue from scrolling through irrelevant listings." },
            { num: "03", title: "Cross-Check Prices Before Ordering", desc: "Prices fluctuate between sellers for identical products. Use your spreadsheet to compare listings and identify the best deals. A five-minute price check can save significant money on larger hauls." },
            { num: "04", title: "Track Your Haul History", desc: "Create a personal tab logging past purchases — what you bought, from which seller, at what price, and quality outcome. Over time this becomes an invaluable reference for reliable sellers." },
            { num: "05", title: "Plan Shipping in Advance", desc: "Estimate total weight and shipping costs before committing. Group items strategically to hit weight thresholds that maximize shipping efficiency for your chosen carrier method." },
            { num: "06", title: "Stay Updated with Community Input", desc: "The Sugargoo community constantly discovers new products. Subscribe to spreadsheet updates so you never miss trending items or limited-time deals across any product category." },
          ].map((tip) => (
            <div key={tip.num} className="flex gap-4">
              <span className="text-2xl font-heading font-bold text-primary-300 flex-shrink-0 leading-none">{tip.num}</span>
              <div>
                <h3 className="text-sm md:text-base font-semibold text-foreground-800 mb-1">{tip.title}</h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{tip.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          Spreadsheet vs Manual Search — Data Comparison
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-background-300">
                <th className="text-left py-3 px-4 font-semibold text-foreground-800">Metric</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-800">Manual Search</th>
                <th className="text-left py-3 px-4 font-semibold text-primary-700 bg-primary-50 rounded-t-lg">Sugargoo Spreadsheet</th>
              </tr>
            </thead>
            <tbody>
              {[
                { metric: "Time to find 10 products", manual: "45-90 minutes", spreadsheet: "10-20 minutes" },
                { metric: "Price comparison coverage", manual: "2-3 sellers per item", spreadsheet: "5-8 sellers per item" },
                { metric: "Size accuracy confidence", manual: "Low — manual checking", spreadsheet: "High — pre-verified sizes" },
                { metric: "Duplicate listing avoidance", manual: "Frequent duplicates", spreadsheet: "Deduplicated entries" },
                { metric: "Trending item discovery", manual: "Random chance", spreadsheet: "Curated community updates" },
                { metric: "Shopping efficiency", manual: "1-3 items per hour", spreadsheet: "10-20 items per hour" },
              ].map((row) => (
                <tr key={row.metric} className="border-b border-background-200/50">
                  <td className="py-3 px-4 font-medium text-foreground-800">{row.metric}</td>
                  <td className="py-3 px-4 text-foreground-500">{row.manual}</td>
                  <td className="py-3 px-4 text-primary-700 bg-primary-50/50 font-medium">{row.spreadsheet}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ErrorMessageSection />
    </>
  );
}